import { useEffect } from 'react';
import {
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { MaintenanceTask } from '../../api/types';
import { useSession } from '../../app/session';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

type FormValues = {
  code: string;
  name: string;
  description: string;
  area: string;
  reference: string;
  interval_mode: string;
  counter_key: string;
  counter_interval: number | '';
  calendar_months: number | '';
  tolerance_counter: number | '';
  tolerance_days: number | '';
  warn_counter_before: number | '';
  warn_days_before: number | '';
  grounds_aircraft: boolean;
  requires_release_to_service: boolean;
  responsible: string;
  is_active: boolean;
};

/**
 * One maintenance task and the interval it runs on.
 *
 * An interval can be counter-based, calendar-based, or both with whichever comes first
 * winning - which is how a 50-hour-or-annual check is actually written in a programme.
 */
export function TaskFormModal({
  opened,
  onClose,
  programmeId,
  aircraftId,
  task,
}: {
  opened: boolean;
  onClose: () => void;
  programmeId: number;
  aircraftId: number;
  task?: MaintenanceTask | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enumChoices } = useSession();

  const counters = useQuery({
    queryKey: ['counters', aircraftId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/counters/', { params: { query: { aircraft: aircraftId } } }),
      ),
    enabled: opened,
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: opened,
  });

  const form = useForm<FormValues>({
    initialValues: {
      code: '',
      name: '',
      description: '',
      area: 'AIRFRAME',
      reference: '',
      interval_mode: 'COUNTER',
      counter_key: 'AIRFRAME_HOURS',
      counter_interval: '',
      calendar_months: '',
      tolerance_counter: '',
      tolerance_days: '',
      warn_counter_before: '',
      warn_days_before: '',
      grounds_aircraft: true,
      requires_release_to_service: false,
      responsible: '',
      is_active: true,
    },
    validate: {
      code: (value) => (value.trim() ? null : t('common.requiredField')),
      name: (value) => (value.trim() ? null : t('common.requiredField')),
      counter_interval: (value, values) =>
        values.interval_mode !== 'CALENDAR' && value === ''
          ? t('common.requiredField')
          : null,
      calendar_months: (value, values) =>
        values.interval_mode !== 'COUNTER' && value === '' ? t('common.requiredField') : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues(
      task
        ? {
            code: task.code,
            name: task.name,
            description: task.description ?? '',
            area: task.area ?? 'AIRFRAME',
            reference: task.reference ?? '',
            interval_mode: task.interval_mode ?? 'COUNTER',
            counter_key: task.counter_key || 'AIRFRAME_HOURS',
            counter_interval: task.counter_interval ? Number(task.counter_interval) : '',
            calendar_months: task.calendar_months ?? '',
            tolerance_counter: task.tolerance_counter ? Number(task.tolerance_counter) : '',
            tolerance_days: task.tolerance_days ?? '',
            warn_counter_before: task.warn_counter_before ? Number(task.warn_counter_before) : '',
            warn_days_before: task.warn_days_before ?? '',
            grounds_aircraft: task.grounds_aircraft ?? true,
            requires_release_to_service: task.requires_release_to_service ?? false,
            responsible: task.responsible ? String(task.responsible) : '',
            is_active: task.is_active ?? true,
          }
        : form.values,
    );
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, task?.id]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = {
        programme: programmeId,
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        description: values.description,
        area: values.area as MaintenanceTask['area'],
        reference: values.reference.trim(),
        interval_mode: values.interval_mode as MaintenanceTask['interval_mode'],
        counter_key: values.interval_mode === 'CALENDAR' ? '' : values.counter_key,
        counter_interval:
          values.counter_interval === '' ? null : String(values.counter_interval),
        calendar_months: values.calendar_months === '' ? null : values.calendar_months,
        tolerance_counter:
          values.tolerance_counter === '' ? '0' : String(values.tolerance_counter),
        tolerance_days: values.tolerance_days === '' ? 0 : values.tolerance_days,
        warn_counter_before:
          values.warn_counter_before === '' ? null : String(values.warn_counter_before),
        warn_days_before: values.warn_days_before === '' ? null : values.warn_days_before,
        grounds_aircraft: values.grounds_aircraft,
        requires_release_to_service: values.requires_release_to_service,
        responsible: values.responsible ? Number(values.responsible) : null,
        is_active: values.is_active,
      };
      if (task) {
        return unwrap(
          await api.PATCH('/api/v1/maintenance/tasks/{id}/', {
            params: { path: { id: task.id } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/maintenance/tasks/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const usesCounter = form.values.interval_mode !== 'CALENDAR';
  const usesCalendar = form.values.interval_mode !== 'COUNTER';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={task ? `${task.code} — ${t('action.edit')}` : t('maintenance.task')}
    >
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <Group grow align="flex-start">
            <TextInput
              label={t('settings.code')}
              required
              placeholder="100H"
              w={120}
              {...form.getInputProps('code')}
            />
            <TextInput label={t('common.name')} required {...form.getInputProps('name')} />
          </Group>

          <Group grow align="flex-start">
            <Select
              label={t('maintenance.area')}
              data={enumChoices('maintenance_area').map((choice) => ({
                value: choice.value,
                label: choice.label,
              }))}
              allowDeselect={false}
              {...form.getInputProps('area')}
            />
            <TextInput
              label={t('maintenance.releaseReference')}
              description={t('maintenance.referenceHint')}
              {...form.getInputProps('reference')}
            />
          </Group>

          <Select
            label={t('maintenance.interval')}
            data={enumChoices('interval_mode').map((choice) => ({
              value: choice.value,
              label: choice.label,
            }))}
            allowDeselect={false}
            {...form.getInputProps('interval_mode')}
          />

          {usesCounter ? (
            <Group grow align="flex-start">
              <Select
                label={t('aircraft.counters')}
                data={(counters.data?.results ?? []).map((counter) => ({
                  value: counter.key,
                  label: counter.label,
                }))}
                allowDeselect={false}
                {...form.getInputProps('counter_key')}
              />
              <NumberInput
                label={t('maintenance.counterInterval')}
                required
                min={0}
                decimalScale={2}
                {...form.getInputProps('counter_interval')}
              />
              <NumberInput
                label={t('maintenance.tolerance')}
                min={0}
                decimalScale={2}
                {...form.getInputProps('tolerance_counter')}
              />
            </Group>
          ) : null}

          {usesCalendar ? (
            <Group grow align="flex-start">
              <NumberInput
                label={t('maintenance.calendarMonths')}
                required
                min={1}
                max={600}
                {...form.getInputProps('calendar_months')}
              />
              <NumberInput
                label={`${t('maintenance.tolerance')} (${t('common.days')})`}
                min={0}
                {...form.getInputProps('tolerance_days')}
              />
            </Group>
          ) : null}

          <Group grow align="flex-start">
            {usesCounter ? (
              <NumberInput
                label={t('maintenance.warnBefore')}
                description={t('alerts.leadTimesHint')}
                min={0}
                decimalScale={2}
                {...form.getInputProps('warn_counter_before')}
              />
            ) : null}
            {usesCalendar ? (
              <NumberInput
                label={`${t('maintenance.warnBefore')} (${t('common.days')})`}
                min={0}
                {...form.getInputProps('warn_days_before')}
              />
            ) : null}
          </Group>

          <Select
            label={t('maintenance.performedBy')}
            description={t('aircraft.maintainers')}
            data={(people.data?.results ?? []).map((person) => ({
              value: String(person.id),
              label: person.full_name,
            }))}
            searchable
            clearable
            {...form.getInputProps('responsible')}
          />

          <Textarea label={t('common.notes')} minRows={2} {...form.getInputProps('description')} />

          <Stack gap={6}>
            <Checkbox
              label={t('maintenance.groundsAircraft')}
              {...form.getInputProps('grounds_aircraft', { type: 'checkbox' })}
            />
            <Checkbox
              label={t('maintenance.requiresRelease')}
              {...form.getInputProps('requires_release_to_service', { type: 'checkbox' })}
            />
            <Checkbox
              label={t('common.active')}
              {...form.getInputProps('is_active', { type: 'checkbox' })}
            />
          </Stack>

          {task?.due_item ? (
            <Text fz="xs" c="dimmed">
              {t('maintenance.remaining')}: {task.due_item.remaining_display}
            </Text>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
